import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { GameCatalogEntry, SessionSummary } from '@perplexicon/api-client';

import { assetUrl } from '../../assets/asset-manifest.js';
import {
  ActiveGameConflictDialog,
  ErrorState,
  GameButton,
  LoadingState,
  PagePlate,
  PlateSectionHeading,
} from '../../components/index.js';
import { isActiveGameConflict, useActiveGameConflict } from '../../lib/active-game-conflict.js';
import { useCurrentUser } from '../../lib/auth.js';
import { getErrorMessage } from '../../lib/errors.js';
import { activeSessionsByGame } from '../../lib/game-meta.js';
import { useActiveSessions, useGameCatalog } from '../../lib/games.js';
import { useStartGameFlow } from '../../lib/start-game-flow.js';
import { isWideCatalogCard } from './catalog-layout.js';
import { GameCatalogCard, GamesPageHeader, ResumeGameCard } from './components/index.js';
import { CreateLobbyDialog } from './CreateLobbyDialog.js';
import { CreateMultiplayerDialog } from './CreateMultiplayerDialog.js';
import { JoinByCodeDialog } from './JoinByCodeDialog.js';

/**
 * The section glyphs. Larger than the profile plate's, to match the `page`
 * heading scale this page's two headings use: they carry a whole region each
 * here, rather than being one of many headings down a long plate.
 */
const SECTION_ICON_CLASS = 'h-10 w-10 shrink-0 object-contain tablet:h-12 tablet:w-12';

/**
 * "Continue Playing" (approved Games-page mockup) — compact resume strips
 * for every session waiting on the player, deliberately quieter than the
 * catalog below it. Rendered only when the player actually has an active
 * session (an empty section here would outrank the catalog it sits above —
 * docs/UI_GUIDELINES.md §8), and never while its own query is still pending,
 * so the section doesn't flash in and back out.
 *
 * No cap or "view all" link: a player holds at most one live session per
 * game per slot (`active-session-limit.ts`), so this list is bounded by the
 * size of the catalog itself and never needs to defer to a fuller list.
 */
function ContinuePlayingSection({ selfId }: { selfId: string }) {
  const activeSessions = useActiveSessions();

  if (!activeSessions.data || activeSessions.data.length === 0) return null;

  return (
    <section className="flex flex-col gap-4">
      <PlateSectionHeading
        title="Continue Playing"
        size="page"
        icon={
          <img
            src={assetUrl('gameModeContinue')}
            alt=""
            aria-hidden="true"
            width={256}
            height={256}
            className={SECTION_ICON_CLASS}
          />
        }
      />
      <div className="grid items-start gap-3 tablet:grid-cols-2">
        {activeSessions.data.map((entry) => (
          <ResumeGameCard key={entry.id} entry={entry} selfId={selfId} />
        ))}
      </div>
    </section>
  );
}

/**
 * "Start a Game" (approved Games-page mockup) — the page's primary content:
 * every registered game as a large illustrated `GameCatalogCard`, two per
 * row from `tablet` up. Driven entirely by the API's catalog endpoint; no
 * game-specific rendering exists here (CLAUDE.md §2).
 *
 * Also handles the "create a multiplayer session for this game" deep link
 * (`?create=multiplayer&game=<id>`) that `GameHostPage`'s "Play again" sends
 * players back to.
 */
function StartAGameSection({
  catalog,
  activeByGame,
  onConflict,
}: {
  catalog: GameCatalogEntry[];
  activeByGame: Map<string, SessionSummary>;
  onConflict: (error: unknown, retry: () => void) => boolean;
}) {
  const { createSolo, isStarting, startSolo, versusGame, setVersusGame, lobbyGame, setLobbyGame } =
    useStartGameFlow(onConflict);
  const [searchParams, setSearchParams] = useSearchParams();

  const multiplayerGameId =
    searchParams.get('create') === 'multiplayer' ? searchParams.get('game') : null;
  const multiplayerGameFromUrl = multiplayerGameId
    ? (catalog.find((entry) => entry.gameId === multiplayerGameId) ?? null)
    : null;
  const activeVersusGame = versusGame ?? multiplayerGameFromUrl;

  function clearMultiplayerSearchParams() {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('create');
    nextParams.delete('game');
    setSearchParams(nextParams, { replace: true });
  }

  return (
    <section className="flex flex-col gap-4">
      <PlateSectionHeading
        title="Start a Game"
        size="page"
        icon={
          <img
            src={assetUrl('gameModeVersus')}
            alt=""
            aria-hidden="true"
            width={256}
            height={256}
            className={SECTION_ICON_CLASS}
          />
        }
      />

      {catalog.length === 0 ? (
        <p className="text-textSecondary">No games are available right now.</p>
      ) : (
        // Grid items stretch by default, which is what lets two cards in one
        // row finish at the same Y coordinate; `items-start` here is what
        // previously let a card with a longer description stand taller than its
        // neighbour. The cards themselves bound every region, so stretching
        // them adds no dead parchment (`GameCatalogCard`).
        <div className="grid gap-5 tablet:grid-cols-2">
          {catalog.map((game, index) => (
            <GameCatalogCard
              key={game.gameId}
              game={game}
              activeSession={activeByGame.get(game.gameId) ?? null}
              isStarting={isStarting(game.gameId)}
              onPlaySolo={() => startSolo(game.gameId)}
              onPlayVersus={() => setVersusGame(game)}
              onHostLobby={() => setLobbyGame(game)}
              layout={isWideCatalogCard(index, catalog.length) ? 'wide' : 'normal'}
            />
          ))}
        </div>
      )}

      {createSolo.isError && !isActiveGameConflict(createSolo.error) && (
        <ErrorState message={getErrorMessage(createSolo.error)} />
      )}

      {activeVersusGame && (
        <CreateMultiplayerDialog
          game={activeVersusGame}
          open
          onOpenChange={(open) => {
            if (!open) {
              setVersusGame(null);
              if (multiplayerGameFromUrl) {
                clearMultiplayerSearchParams();
              }
            }
          }}
          onActiveGameConflict={onConflict}
        />
      )}

      {lobbyGame && (
        <CreateLobbyDialog
          game={lobbyGame}
          open={lobbyGame !== null}
          onOpenChange={(open) => {
            if (!open) setLobbyGame(null);
          }}
          onActiveGameConflict={onConflict}
        />
      )}
    </section>
  );
}

/**
 * The game catalog / Games page (APP_SPEC §17, §18, §19; approved
 * Games-page mockup). The visual hierarchy is deliberately Header ->
 * (quiet) Continue Playing -> (dominant) Start a Game: active sessions are
 * compact resume strips, never full game cards, so the catalog stays the
 * page's primary content.
 *
 * The page sits in the same master parchment shell the profile uses — a
 * `PageFrame` plate with the brass page corners, on a `flush` `PageLayout` so
 * no band of bare parchment shows outside the frame. Both pages therefore
 * declare the same plate radius, the same corner sizes and the same interior
 * padding; they are one shell system, not two pages that happen to be beige.
 */
export function CatalogPage() {
  const { data: user } = useCurrentUser();
  const catalog = useGameCatalog();
  const activeSessions = useActiveSessions();
  const [joinByCodeOpen, setJoinByCodeOpen] = useState(false);

  // One conflict handler for every way into a game on this page — starting
  // solo, inviting an opponent, hosting a lobby, joining by code (APP_SPEC
  // §21.1). Owned here rather than inside each dialog so only one
  // resume-or-abandon dialog can ever be open.
  const conflict = useActiveGameConflict();

  if (catalog.isPending) {
    return <LoadingState label="Loading games…" />;
  }

  if (catalog.isError) {
    return (
      <ErrorState message={getErrorMessage(catalog.error)} onRetry={() => void catalog.refetch()} />
    );
  }

  if (!user) {
    return <ErrorState message="Sign in to view the games catalog." />;
  }

  const activeByGame = activeSessionsByGame(activeSessions.data ?? []);

  return (
    <PagePlate
      overlays={
        <>
          <JoinByCodeDialog
            open={joinByCodeOpen}
            onOpenChange={setJoinByCodeOpen}
            onActiveGameConflict={conflict.capture}
          />

          <ActiveGameConflictDialog
            conflict={conflict.conflict}
            onAbandon={conflict.abandonAndRetry}
            onDismiss={conflict.dismiss}
            isAbandoning={conflict.isAbandoning}
            abandonError={conflict.abandonError}
          />
        </>
      }
    >
      <GamesPageHeader
        actions={
          <GameButton size="lg" onClick={() => setJoinByCodeOpen(true)}>
            Join with Code
          </GameButton>
        }
      />

      <ContinuePlayingSection selfId={user.id} />

      <StartAGameSection
        catalog={catalog.data}
        activeByGame={activeByGame}
        onConflict={conflict.capture}
      />
    </PagePlate>
  );
}
